import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Chip,
  Divider,
  IconButton,
  alpha,
} from "@mui/material";
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { useChat } from "../contexts/ChatProvider";
import { ChatUser } from "../types/chat";
import api from "../infra/api";
import { searchCandidatesByName } from "../hooks/useCandidate";
import { getPhonesByType, PhoneType } from "../hooks/usePhones";

interface ModalNewChatProps {
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chat-tabpanel-${index}`}
      aria-labelledby={`chat-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface CandidateResult {
  id: number;
  pessoal_nome: string;
  pessoal_cidade?: string;
  pessoal_bairro?: string;
}

const ModalNewChat: React.FC<ModalNewChatProps> = ({ open, onClose }) => {
  const { createSession, setActiveSession } = useChat();
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Internal chat states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ChatUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<ChatUser[]>([]);
  const [isGroupChat, setIsGroupChat] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // External chat states (candidates)
  const [candidateSearchQuery, setCandidateSearchQuery] = useState("");
  const [candidateResults, setCandidateResults] = useState<CandidateResult[]>([]);
  const [searchingCandidates, setSearchingCandidates] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateResult | null>(null);
  const [candidatePhones, setCandidatePhones] = useState<PhoneType[]>([]);
  const [loadingPhones, setLoadingPhones] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState<PhoneType | null>(null);

  // Buscar usuários internos
  const searchUsers = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const apiUrl =
        process.env.REACT_APP_API_URL +
        `api/chat/users/search?q=${encodeURIComponent(query)}`;
      const response = await api(apiUrl);

      if (response.ok) {
        const users = await response.json();
        setSearchResults(users);
      }
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
    } finally {
      setSearching(false);
    }
  };

  // Buscar candidatos externos
  const searchCandidates = async (query: string) => {
    if (query.length < 2) {
      setCandidateResults([]);
      return;
    }

    setSearchingCandidates(true);
    try {
      const results = await searchCandidatesByName(query);
      setCandidateResults(results);
    } catch (error) {
      console.error("Erro ao buscar candidatos:", error);
    } finally {
      setSearchingCandidates(false);
    }
  };

  // Carregar telefones do candidato
  const loadCandidatePhones = async (candidateId: number) => {
    setLoadingPhones(true);
    try {
      const phones = await getPhonesByType(candidateId, "PESSOAL");
      if (phones && Array.isArray(phones)) {
        // Filtrar apenas telefones com WhatsApp
        const whatsappPhones = phones.filter(
          (phone) => phone.whatsapp === 1 || phone.whatsapp === "1" || phone.whatsapp === true
        );
        setCandidatePhones(whatsappPhones.length > 0 ? whatsappPhones : phones);
      } else {
        setCandidatePhones([]);
      }
    } catch (error) {
      console.error("Erro ao carregar telefones:", error);
      setCandidatePhones([]);
    } finally {
      setLoadingPhones(false);
    }
  };

  // Debounce da busca de usuários
  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsers(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounce da busca de candidatos
  useEffect(() => {
    const timer = setTimeout(() => {
      searchCandidates(candidateSearchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [candidateSearchQuery]);

  // Carregar telefones quando candidato é selecionado
  useEffect(() => {
    if (selectedCandidate) {
      loadCandidatePhones(selectedCandidate.id);
    }
  }, [selectedCandidate]);

  const handleUserToggle = (user: ChatUser) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((u) => u.id === user.id);
      if (isSelected) {
        return prev.filter((u) => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleSelectCandidate = (candidate: CandidateResult) => {
    setSelectedCandidate(candidate);
    setSelectedPhone(null);
    setCandidatePhones([]);
  };

  const handleBackToSearch = () => {
    setSelectedCandidate(null);
    setSelectedPhone(null);
    setCandidatePhones([]);
  };

  const handleCreateInternalChat = async () => {
    if (selectedUsers.length === 0) return;
    // Validações
    if (isGroupChat && !groupName.trim()) {
      setError("Nome do grupo é obrigatório");
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const participantIds = selectedUsers.map((u) => u.id);
      const sessionType =
        isGroupChat || selectedUsers.length > 1 ? "group" : "private";
      const sessionName =
        sessionType === "group" ? groupName.trim() : undefined;

      // Criar a sessão no banco de dados
      const newSession = await createSession(
        participantIds,
        sessionName,
        sessionType
      );

      // Automaticamente abrir a conversa criada
      if (newSession) {
        setActiveSession(newSession);
        console.log("Conversa criada e aberta:", newSession);
      }

      handleClose();
    } catch (error) {
      console.error("Erro ao criar conversa:", error);
      setError("Erro ao criar conversa. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartExternalChat = async () => {
    if (!selectedCandidate || !selectedPhone) return;

    setError(null);
    setLoading(true);
    try {
      // Formatar número de telefone para WhatsApp
      const phoneNumber = `${selectedPhone.telefone_ddd}${selectedPhone.telefone_numero}`.replace(/\D/g, "");
      
      // Criar sessão externa com metadados do candidato
      const apiUrl = process.env.REACT_APP_API_URL + "api/chat/sessions/external";
      const response = await api(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_name: selectedCandidate.pessoal_nome,
          session_type: "external",
          external_type: "whatsapp",
          external_identifier: phoneNumber,
          candidate_id: selectedCandidate.id,
          candidate_name: selectedCandidate.pessoal_nome,
          phone_number: phoneNumber,
        }),
      });

      if (response.ok) {
        const newSession = await response.json();
        setActiveSession(newSession);
        console.log("Conversa externa criada:", newSession);
        handleClose();
      } else {
        throw new Error("Erro ao criar conversa externa");
      }
    } catch (error) {
      console.error("Erro ao criar conversa externa:", error);
      setError("Erro ao iniciar conversa. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    setSearchResults([]);
    setSelectedUsers([]);
    setIsGroupChat(false);
    setGroupName("");
    setError(null);
    setTabValue(0);
    setCandidateSearchQuery("");
    setCandidateResults([]);
    setSelectedCandidate(null);
    setCandidatePhones([]);
    setSelectedPhone(null);
    onClose();
  };

  const formatPhoneNumber = (phone: PhoneType) => {
    const ddd = phone.telefone_ddd || "";
    const numero = phone.telefone_numero || "";
    return `(${ddd}) ${numero}`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonAddIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Nova Conversa
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Mensagem de erro */}
          {error && (
            <Box
              p={1.5}
              bgcolor="error.light"
              color="error.contrastText"
              borderRadius={1}
              fontSize="0.875rem"
            >
              {error}
            </Box>
          )}

          {/* Tabs para Interno/Externo */}
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.95rem",
              },
            }}
          >
            <Tab
              icon={<PersonIcon />}
              iconPosition="start"
              label="Interno"
              id="chat-tab-0"
            />
            <Tab
              icon={<WhatsAppIcon />}
              iconPosition="start"
              label="Externo"
              id="chat-tab-1"
            />
          </Tabs>

          {/* Tab Interno - Usuários do Sistema */}
          <TabPanel value={tabValue} index={0}>
            {/* Switch para grupo */}
            <FormControlLabel
              control={
                <Switch
                  checked={isGroupChat}
                  onChange={(e) => setIsGroupChat(e.target.checked)}
                />
              }
              label="Conversa em grupo"
            />

            {/* Nome do grupo (se for grupo) */}
            {isGroupChat && (
              <TextField
                fullWidth
                label="Nome do grupo"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Digite o nome do grupo..."
                sx={{ mt: 2 }}
              />
            )}

            {/* Campo de busca */}
            <TextField
              fullWidth
              label="Buscar pessoas"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Digite o nome ou email..."
              sx={{ mt: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {searching ? <CircularProgress size={20} /> : <SearchIcon />}
                  </InputAdornment>
                ),
              }}
            />

            {/* Usuários selecionados */}
            {selectedUsers.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Selecionados ({selectedUsers.length}):
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {selectedUsers.map((user) => (
                    <Chip
                      key={user.id}
                      avatar={
                        <Avatar sx={{ width: 24, height: 24 }}>
                          {user.nome?.charAt(0) || "U"}
                        </Avatar>
                      }
                      label={user.nome || user.nomecomp}
                      onDelete={() => handleUserToggle(user)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Resultados da busca */}
            <List sx={{ maxHeight: 300, overflow: "auto", mt: 1 }}>
              {searchResults.map((user) => {
                const isSelected = selectedUsers.some((u) => u.id === user.id);
                return (
                  <ListItem
                    key={user.id}
                    dense
                    button
                    onClick={() => handleUserToggle(user)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      bgcolor: isSelected ? alpha("#1976d2", 0.08) : "transparent",
                    }}
                  >
                    <Checkbox checked={isSelected} tabIndex={-1} disableRipple />
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "primary.main" }}>
                        {user.nome?.charAt(0) || "U"}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={user.nome || user.nomecomp}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                          {user.cargo && (
                            <Typography variant="caption" color="text.secondary">
                              {user.cargo}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
              {searchQuery.length >= 2 &&
                searchResults.length === 0 &&
                !searching && (
                  <ListItem>
                    <ListItemText
                      primary="Nenhum usuário encontrado"
                      secondary="Tente uma busca diferente"
                    />
                  </ListItem>
                )}
            </List>
          </TabPanel>

          {/* Tab Externo - Candidatos via WhatsApp */}
          <TabPanel value={tabValue} index={1}>
            {!selectedCandidate ? (
              <>
                {/* Busca de candidatos */}
                <TextField
                  fullWidth
                  label="Buscar candidato"
                  value={candidateSearchQuery}
                  onChange={(e) => setCandidateSearchQuery(e.target.value)}
                  placeholder="Digite o nome do candidato..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {searchingCandidates ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SearchIcon />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Lista de candidatos */}
                <List sx={{ maxHeight: 350, overflow: "auto", mt: 1 }}>
                  {candidateResults.map((candidate) => (
                    <ListItem
                      key={candidate.id}
                      button
                      onClick={() => handleSelectCandidate(candidate)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        border: "1px solid",
                        borderColor: "divider",
                        "&:hover": {
                          bgcolor: alpha("#25D366", 0.08),
                          borderColor: "#25D366",
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: "#25D366" }}>
                          {candidate.pessoal_nome?.charAt(0) || "C"}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography fontWeight={500}>
                            {candidate.pessoal_nome}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {[candidate.pessoal_cidade, candidate.pessoal_bairro]
                              .filter(Boolean)
                              .join(" - ")}
                          </Typography>
                        }
                      />
                      <WhatsAppIcon sx={{ color: "#25D366", ml: 1 }} />
                    </ListItem>
                  ))}
                  {candidateSearchQuery.length >= 2 &&
                    candidateResults.length === 0 &&
                    !searchingCandidates && (
                      <ListItem>
                        <ListItemText
                          primary="Nenhum candidato encontrado"
                          secondary="Tente uma busca diferente"
                        />
                      </ListItem>
                    )}
                  {candidateSearchQuery.length < 2 && (
                    <Box textAlign="center" py={4} color="text.secondary">
                      <GroupsIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
                      <Typography>
                        Digite pelo menos 2 caracteres para buscar candidatos
                      </Typography>
                    </Box>
                  )}
                </List>
              </>
            ) : (
              <>
                {/* Candidato selecionado - mostrar telefones */}
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <IconButton onClick={handleBackToSearch} size="small">
                    <ArrowBackIcon />
                  </IconButton>
                  <Avatar sx={{ bgcolor: "#25D366" }}>
                    {selectedCandidate.pessoal_nome?.charAt(0) || "C"}
                  </Avatar>
                  <Box>
                    <Typography fontWeight={600}>
                      {selectedCandidate.pessoal_nome}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Selecione um contato WhatsApp
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {loadingPhones ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : candidatePhones.length > 0 ? (
                  <List>
                    {candidatePhones.map((phone) => {
                      const isWhatsApp =
                        phone.whatsapp === 1 ||
                        phone.whatsapp === "1" ||
                        phone.whatsapp === true;
                      const isSelected =
                        selectedPhone?.telefone_codigo === phone.telefone_codigo;

                      return (
                        <ListItem
                          key={phone.telefone_codigo}
                          button
                          onClick={() => setSelectedPhone(phone)}
                          sx={{
                            borderRadius: 2,
                            mb: 1,
                            border: "2px solid",
                            borderColor: isSelected ? "#25D366" : "divider",
                            bgcolor: isSelected
                              ? alpha("#25D366", 0.08)
                              : "transparent",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: "#25D366",
                              bgcolor: alpha("#25D366", 0.05),
                            },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor: isWhatsApp ? "#25D366" : "grey.400",
                              }}
                            >
                              {isWhatsApp ? <WhatsAppIcon /> : <PhoneIcon />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography fontWeight={500}>
                                  {formatPhoneNumber(phone)}
                                </Typography>
                                {isWhatsApp && (
                                  <Chip
                                    label="WhatsApp"
                                    size="small"
                                    sx={{
                                      bgcolor: "#25D366",
                                      color: "white",
                                      fontSize: "0.7rem",
                                      height: 20,
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {phone.telefone_tipo || "Celular"}
                                {phone.telefone_contato &&
                                  ` • ${phone.telefone_contato}`}
                              </Typography>
                            }
                          />
                          {isSelected && (
                            <Checkbox checked tabIndex={-1} disableRipple />
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Box textAlign="center" py={4} color="text.secondary">
                    <PhoneIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
                    <Typography>
                      Nenhum telefone cadastrado para este candidato
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Cadastre um telefone no perfil do candidato
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </TabPanel>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancelar
        </Button>
        {tabValue === 0 ? (
          <Button
            variant="contained"
            onClick={handleCreateInternalChat}
            disabled={selectedUsers.length === 0 || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <PersonIcon />}
          >
            {loading ? "Criando..." : "Criar Conversa"}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleStartExternalChat}
            disabled={!selectedCandidate || !selectedPhone || loading}
            startIcon={
              loading ? <CircularProgress size={16} /> : <WhatsAppIcon />
            }
            sx={{
              bgcolor: "#25D366",
              "&:hover": {
                bgcolor: "#128C7E",
              },
            }}
          >
            {loading ? "Iniciando..." : "Iniciar Conversa"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ModalNewChat;
